import {
  type Facility,
  RXDB_FACILITY_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/facility";
import {
  type Referral,
  RXDB_REFERRAL_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/referral";
import { useMemo } from "react";
import { useRxData } from "rxdb-hooks";
import type { ImagingStudyWithImages } from "./use-patient-imaging-studies";

export const usePatientReferrals = (
  patientId: string,
  imagingStudies: ImagingStudyWithImages[],
) => {
  const { result: referrals } = useRxData<Referral>(
    RXDB_REFERRAL_COLLECTION_NAME,
    (collection) =>
      collection.find({
        selector: {
          patientId: {
            $eq: patientId,
          },
        },
        sort: [{ "meta.updatedAt": "desc" }],
        limit: 100,
      }),
  );

  const facilityIds = useMemo(
    () =>
      referrals
        .map((r) => r.facilityId)
        .filter((id): id is string => id != null),
    [referrals],
  );

  const { result: facilities } = useRxData<Facility>(
    RXDB_FACILITY_COLLECTION_NAME,
    facilityIds.length > 0
      ? (collection) =>
          collection.find({
            selector: {
              id: {
                $in: facilityIds,
              },
            },
          })
      : undefined,
  );

  return referrals.map((referralRow) => {
    const referral = referralRow._data;
    const facility = referral.facilityId
      ? facilities.find((f) => f.id === referral.facilityId)
      : undefined;
    const facilityName =
      referral.freeTextFacility || facility?.name || undefined;

    return {
      ...referral,
      facilityName,
      meta: {
        ...referral.meta,
        created: referral.meta.createdAt,
      },
      allImagingStudies: imagingStudies,
    };
  });
};
